import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface StockItem {
  _id: string;
  medicineId: string;
  name: string;
  genericName?: string;
  category:
    | "tablet"
    | "capsule"
    | "syrup"
    | "injection"
    | "cream"
    | "drops"
    | "inhaler"
    | "other";
  manufacturer: string;
  supplier: {
    _id: string;
    name: string;
    contactPerson: string;
    email: string;
    phone: string;
  };
  batchNumber: string;
  expiryDate: string;
  manufacturingDate: string;
  quantity: {
    current: number;
    minimum: number;
    unit: "pieces" | "bottles" | "boxes" | "strips" | "vials";
  };
  pricing: {
    purchasePrice: number;
    sellingPrice: number;
    mrp: number;
    discount: number;
    taxPercent?: number;
    tax?: number;
  };
  medicine?: any;
  hsnCode?: string;
  gstRate?: number;
  location?: {
    rack?: string;
    shelf?: string;
    position?: string;
  };
  isActive: boolean;
  alertLowStock: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StockState {
  stockItems: StockItem[];
  currentStockItem: StockItem | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;
  alerts: {
    lowStock: StockItem[];
    expiring: StockItem[];
    expired: StockItem[];
    summary: {
      lowStockCount: number;
      expiringCount: number;
      expiredCount: number;
    };
  } | null;
  stats: {
    totalItems: number;
    lowStockItems: number;
    expiredItems: number;
    categoryBreakdown: Array<{ _id: string; count: number }>;
    stockValue: {
      totalPurchaseValue: number;
      totalSellingValue: number;
    };
  } | null;
}

const initialState: StockState = {
  stockItems: [],
  currentStockItem: null,
  loading: false,
  error: null,
  pagination: null,
  alerts: null,
  stats: null,
};

const stockSlice = createSlice({
  name: "stock",
  initialState,
  reducers: {
    // Get stock items actions
    getStockItemsStart: (
      state,
      action: PayloadAction<{
        page?: number;
        limit?: number;
        search?: string;
        category?: string;
        supplier?: string;
        lowStock?: string;
        expired?: string;
        sortBy?: string;
        sortOrder?: string;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getStockItemsSuccess: (
      state,
      action: PayloadAction<{
        data: StockItem[];
        pagination: any;
      }>
    ) => {
      state.loading = false;
      state.stockItems = action.payload.data;
      state.pagination = action.payload.pagination;
      state.error = null;
    },
    getStockItemsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Get single stock item actions
    getStockItemStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    getStockItemSuccess: (state, action: PayloadAction<StockItem>) => {
      state.loading = false;
      state.currentStockItem = action.payload;
      state.error = null;
    },
    getStockItemFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Create stock item actions
    createStockItemStart: (state, action: PayloadAction<any>) => {
      state.loading = true;
      state.error = null;
    },
    createStockItemSuccess: (state, action: PayloadAction<StockItem>) => {
      state.loading = false;
      state.stockItems.unshift(action.payload);
      state.error = null;
    },
    createStockItemFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update stock item actions
    updateStockItemStart: (
      state,
      action: PayloadAction<{ id: string; data: any }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateStockItemSuccess: (state, action: PayloadAction<StockItem>) => {
      state.loading = false;
      const index = state.stockItems.findIndex(
        (item) => item._id === action.payload._id
      );
      if (index !== -1) {
        state.stockItems[index] = action.payload;
      }
      if (state.currentStockItem?._id === action.payload._id) {
        state.currentStockItem = action.payload;
      }
      state.error = null;
    },
    updateStockItemFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update stock quantity actions
    updateStockQuantityStart: (
      state,
      action: PayloadAction<{
        id: string;
        quantity: number;
        operation: "add" | "subtract";
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateStockQuantitySuccess: (state, action: PayloadAction<StockItem>) => {
      state.loading = false;
      const index = state.stockItems.findIndex(
        (item) => item._id === action.payload._id
      );
      if (index !== -1) {
        state.stockItems[index] = action.payload;
      }
      if (state.currentStockItem?._id === action.payload._id) {
        state.currentStockItem = action.payload;
      }
      state.error = null;
    },
    updateStockQuantityFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete stock item actions
    deleteStockItemStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    deleteStockItemSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.stockItems = state.stockItems.filter(
        (item) => item._id !== action.payload
      );
      if (state.currentStockItem?._id === action.payload) {
        state.currentStockItem = null;
      }
      state.error = null;
    },
    deleteStockItemFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Get stock alerts actions
    getStockAlertsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    getStockAlertsSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.alerts = action.payload;
      state.error = null;
    },
    getStockAlertsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Get stock stats actions
    getStockStatsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    getStockStatsSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.stats = action.payload;
      state.error = null;
    },
    getStockStatsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Clear current stock item
    clearCurrentStockItem: (state) => {
      state.currentStockItem = null;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  getStockItemsStart,
  getStockItemsSuccess,
  getStockItemsFailure,
  getStockItemStart,
  getStockItemSuccess,
  getStockItemFailure,
  createStockItemStart,
  createStockItemSuccess,
  createStockItemFailure,
  updateStockItemStart,
  updateStockItemSuccess,
  updateStockItemFailure,
  updateStockQuantityStart,
  updateStockQuantitySuccess,
  updateStockQuantityFailure,
  deleteStockItemStart,
  deleteStockItemSuccess,
  deleteStockItemFailure,
  getStockAlertsStart,
  getStockAlertsSuccess,
  getStockAlertsFailure,
  getStockStatsStart,
  getStockStatsSuccess,
  getStockStatsFailure,
  clearCurrentStockItem,
  clearError,
} = stockSlice.actions;

export default stockSlice.reducer;
